import React, { PureComponent, MouseEvent, Fragment } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { ArrowDropDownCircle as ArrowIcon } from '@mui/icons-material';
import { ProductMenuContainer, ProductName } from './styles';
import {
  IState,
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import { Routes } from '../../../constants';
import { IAppState } from '../../../state/reducers';
import {
  Product, updateProduct, toggleOpen, cacheProduct,
} from '../../../state/product';
import { finishImpersonation, isImpersonating } from '../../../utils/impersonation';
import { queryGamesAction } from '../../../state/search';
import { queryGames } from '../../../state/exchange/searchResults';
import { clearAllFilters } from '../../../state/filterAssets';
import { selectIsUserVaultSuperAdmin } from '../../../state/user/userOrgRole';

class ProductMenu extends PureComponent<IProps, IState> {
  state: Readonly<IState> = {
    anchorEl: null,
  };

  openMenu = (e: MouseEvent) => {
    const { onMenuToggle } = this.props;
    this.setState({
      anchorEl: e.target,
    });
    onMenuToggle();
  };

  selectProduct = async (product: Product) => {
    const {
      navigate,
      location: {
        pathname: route,
      },
      product: currentProduct,
      onSelectProduct,
      onSearch,
      onSearchGames,
      onClearAllFilters,
      isVaultSuperAdmin,
    } = this.props;

    // Impersonation is an Exchange-only tool, so moving to another product ends the
    // session rather than carrying it across. finishImpersonation reloads, which is what
    // puts the admin's own token back in play - the cached choice above is what lands
    // them on the product they clicked once they are themselves again.
    if (product !== currentProduct && isImpersonating()) {
      cacheProduct(product);
      await finishImpersonation();
      return;
    }

    onSelectProduct(product, isVaultSuperAdmin);

    // redirect to home if selected product does not have access to exchange search screen
    if (product !== Product.Exchange && route === Routes.EXCHANGE) {
      navigate(Routes.HOME);
    }

    if (route === Routes.HOME) {
      onClearAllFilters();
      onSearch();
    } else if (route === Routes.EXCHANGE) {
      onSearchGames();
    }
  };

  render() {
    const { anchorEl } = this.state;
    const {
      isOpen,
      product,
      available,
      onMenuToggle,
    } = this.props;
    return (
      <ProductMenuContainer>
        <ProductName
          data-auto="ProdName"
        >
          { product }
        </ProductName>
        {available.length > 1 && (
          <>
            <IconButton onClick={(e) => this.openMenu(e)} data-auto="ProdMenuBtn" size="large">
              <ArrowIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              open={isOpen}
              data-auto="ProdMenuList"
              onClose={onMenuToggle}
            >
              {available.map((value) => (
                <MenuItem
                  data-auto="ProdMenuItem"
                  data-prodname={value}
                  key={value}
                  onClick={() => this.selectProduct(value)}
                  style={{
                    fontWeight: product === value ? 'bolder' : 'inherit',
                  }}
                >
                  {value}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </ProductMenuContainer>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isOpen: state.product.isOpen,
  product: state.product.target,
  available: state.product.available,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
});

const mapDispatchToProps: IDispatchProps = {
  onMenuToggle: toggleOpen,
  onSelectProduct: updateProduct,
  onSearch: queryGamesAction,
  onSearchGames: queryGames,
  onClearAllFilters: clearAllFilters,
};

const ConnectedProductMenu = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProductMenu);

// Wrapper component to provide router hooks
const ProductMenuWithRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return <ConnectedProductMenu navigate={navigate} location={location} />;
};

export default ProductMenuWithRouter;
